import * as React from 'react';
import Button from 'reactstrap/lib/Button';
import { SlideOver } from './SlideOver';
import * as Msdyn365 from '@msdyn365-commerce/core';

const isIframeSupported = () => {
    return true;
    // try {
    //     const { protocol, hostname } = window.location;
    //     return protocol === 'https:' && hostname !== 'localhost';
    // } catch {
    //     return false;
    // }
};

export interface ISignInComponentProps {
    /**
     * Button text to display
     */
    buttonText?: string;

    /**
     * CSS class for the button
     */
    buttonClassName?: string;

    /**
     * CSS class for the SlideOver container
     */
    slideOverClassName?: string;

    /**
     * Width of the SlideOver in pixels
     */
    slideOverWidth?: number;

    /**
     * Title for the sign-in dialog/drawer
     */
    drawerTitle?: string;

    /**
     * Return URL after authentication
     */
    returnUrl?: string;

    moduleProps: Msdyn365.IModule;

    /**
     * Callback when authentication flow is completed
     */
    onAuthComplete?: () => void;

    /**
     * Additional props to pass to the button
     */
    buttonProps?: React.ComponentProps<typeof Button>;

    /**
     * Renders a custom trigger in place of the default sign-in button.
     * Receives the callback that opens the drawer.
     */
    renderTrigger?(open: () => void): React.ReactNode;
}

/**
 * Reusable Sign In Component
 * A complete, self-contained sign-in solution with authentication flow, polling, and dialog management
 */
export const SignInComponent: React.FC<ISignInComponentProps> = ({
    buttonText,
    slideOverClassName,
    slideOverWidth = 520,
    drawerTitle,
    onAuthComplete,
    buttonClassName,
    moduleProps,
    buttonProps = {},
    renderTrigger
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const pollId = React.useRef<number | undefined>(undefined);
    const flowRef = React.useRef<'reset' | 'signupsignin' | null>(null);
    flowRef.current = 'signupsignin';

    // Generate nonce for security
    const generateNonce = React.useCallback((): string => {
        if (typeof crypto !== 'undefined') {
            if (typeof (crypto as any).randomUUID === 'function') {
                return (crypto as any).randomUUID();
            }
            if (typeof (crypto as any).getRandomValues === 'function') {
                const array = new Uint32Array(4);
                (crypto as any).getRandomValues(array);
                return Array.from(array, n => n.toString(16)).join('');
            }
        }
        return String(Date.now() + Math.random());
    }, []);

    const authority = moduleProps.context.app.config.b2cAuthority;
    const policyId = moduleProps.context.app.config.b2cSignInPolicy;
    const clientId = moduleProps.context.app.config.b2cClientId;
    const redirectUri = moduleProps.context.app.config.b2cRedirectUri;
    const returnUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    const passwordResetPolicy = moduleProps.context.app.config.b2cPasswordResetPolicy;
    const passwordResetRedirectUri = moduleProps.context.app.config.b2cPasswordRedirectUri;
    const locale = moduleProps.context.request?.locale || 'en-US';

    const authorityBase = React.useMemo(() => {
        return authority.replace(/\/$/, '');
    }, [authority]);

    const tenantFromAuthority = React.useMemo(() => {
        try {
            const u = new URL(authorityBase);
            const parts = u.pathname.split('/').filter(Boolean);
            return parts[parts.length - 1] || '';
        } catch {
            return '';
        }
    }, [authorityBase]);

    const b2cHostFromAuthority = React.useMemo(() => {
        try {
            const u = new URL(authorityBase);
            return u.host;
        } catch {
            return '';
        }
    }, [authorityBase]);
    // Generate the authorize URL
    const getAuthorizeUrl = React.useCallback((): string => {
        const base = `https://${b2cHostFromAuthority}/${tenantFromAuthority}/oauth2/v2.0/authorize`;
        const q = new URLSearchParams({
            p: policyId || '',
            client_id: clientId || '',
            response_type: 'code id_token',
            response_mode: 'form_post',
            redirect_uri: redirectUri || '',
            scope: 'openid profile offline_access',
            state: returnUrl || '',
            nonce: generateNonce(),
            ui_locales: locale
        });
        return `${base}?${q.toString()}`;
    }, [b2cHostFromAuthority, tenantFromAuthority, clientId, redirectUri, returnUrl, generateNonce, moduleProps.context.request?.locale]);

    const buildPasswordResetUrl = React.useCallback(
        (loginHint?: string) => {
            const base = `https://${b2cHostFromAuthority}/${tenantFromAuthority}/oauth2/v2.0/authorize`;
            const q = new URLSearchParams({
                p: passwordResetPolicy,
                client_id: clientId,
                response_type: 'id_token',
                response_mode: 'form_post',
                redirect_uri: redirectUri,
                scope: 'openid',
                nonce: 'defaultNonce',
                prompt: 'login',
                ui_locales: locale
            });
            if (loginHint) {
                q.set('login_hint', loginHint);
            }
            return `${base}?${q.toString()}`;
        },
        [
            b2cHostFromAuthority,
            tenantFromAuthority,
            passwordResetPolicy,
            clientId,
            passwordResetRedirectUri,
            moduleProps.context.request?.locale
        ]
    );

    // Check if iframe is same-origin (authentication completed)
    const sameOriginCheck = React.useCallback((): boolean => {
        const frame = iframeRef.current;
        if (!frame) {
            return false;
        }
        try {
            const href = frame.contentWindow?.location?.href ?? '';
            if (!href) {
                return false;
            }
            const same = href.startsWith(`${window.location.origin}/`);
            if (same) {
                return true;
            }
        } catch {
            // Ignore cross-origin access errors
        }
        return false;
    }, []);

    // Handle authentication flow completion
    const onSignInFlowDone = React.useCallback(() => {
        setIsOpen(false);
        // Prevent loop.
        const here = location.pathname.toLowerCase();
        if (here.startsWith('/_msdyn365/authresp')) {
            return;
        }
        console.log('Sign-in flow completed.');
        // Call the completion callback if provided
        onAuthComplete?.();

        // Auth callback to set cookies.
        window.location.replace('/_msdyn365/signin');
    }, [onAuthComplete]);

    const navigateIframe = React.useCallback((url: string) => {
        if (iframeRef.current) {
            iframeRef.current.src = url;
        }
    }, []);

    // Handle visibility change for polling
    const onVisibilityChange = React.useCallback(() => {
        if (document.visibilityState === 'visible') {
            if (sameOriginCheck()) {
                onSignInFlowDone();
            }
        }
    }, [sameOriginCheck, onSignInFlowDone]);

    // Stop polling and remove event listeners
    const stopPollingAndListeners = React.useCallback(() => {
        if (pollId.current) {
            window.clearInterval(pollId.current);
            pollId.current = undefined;
        }
        window.removeEventListener('visibilitychange', onVisibilityChange);
    }, [onVisibilityChange]);

    // Handle opening the authentication dialog
    const handleOpenAuth = () => {
        if (isIframeSupported()) {
            setIsOpen(true);
        } else {
            const url = getAuthorizeUrl();
            window.location.assign(url);
        }
    };

    // Handle closing the authentication dialog
    const handleCloseAuth = () => {
        setIsOpen(false);
        stopPollingAndListeners();
    };

    // Effect to handle dialog state changes and polling
    React.useEffect(() => {
        if (isOpen) {
            // Check if we are already signed in.
            if (sameOriginCheck()) {
                onSignInFlowDone();
                return;
            }

            // Start polling.
            pollId.current = window.setInterval(() => {
                if (sameOriginCheck()) {
                    if (flowRef.current === 'signupsignin') {
                        onSignInFlowDone();
                    } else if (flowRef.current === 'reset') {
                        navigateIframe(passwordResetRedirectUri);
                    }
                }
            }, 500);

            window.addEventListener('visibilitychange', onVisibilityChange);
        } else {
            stopPollingAndListeners();
        }

        return () => {
            if (isOpen) {
                stopPollingAndListeners();
            }
        };
    }, [isOpen, sameOriginCheck, onSignInFlowDone, onVisibilityChange, stopPollingAndListeners]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            stopPollingAndListeners();
        };
    }, [stopPollingAndListeners]);

    React.useEffect(() => {
        const onMsg = (ev: MessageEvent) => {
            const data = (ev?.data ?? {}) as { type?: string; loginHint?: string };
            let url: string | undefined;

            switch (data.type) {
                case 'B2C_FORGOT_PASSWORD':
                    flowRef.current = 'reset';
                    url = buildPasswordResetUrl(data.loginHint);
                    break;

                case 'B2C_CANCEL':
                    flowRef.current = 'signupsignin';
                    url = getAuthorizeUrl();
                    break;
            }

            if (url !== undefined) {
                navigateIframe(url); // Navigate iframe to appropriate flow
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, [buildPasswordResetUrl, navigateIframe]);

    return (
        <>
            {renderTrigger ? (
                renderTrigger(handleOpenAuth)
            ) : (
                <Button className={buttonClassName} onClick={handleOpenAuth} aria-label={buttonText} {...buttonProps}>
                    <span className='gh-header__signin-text'>{buttonText}</span>
                </Button>
            )}
            <SlideOver open={isOpen} onClose={handleCloseAuth} title={drawerTitle} widthPx={slideOverWidth} className={slideOverClassName}>
                <iframe
                    ref={iframeRef}
                    title={drawerTitle}
                    src={getAuthorizeUrl()}
                    className='gh-signin-component'
                    allow='publickey-credentials-get *; storage-access *'
                />
            </SlideOver>
        </>
    );
};
